import React, { useId } from "react";

import { CATALOG_TEXT, FILTER_MAX_CHARS, STATE_OPTIONS, VIEW_OPTIONS } from "./catalogText";

const F = CATALOG_TEXT.filters;
export const FILTER_LABEL = "text-[11px] font-semibold uppercase tracking-wide text-gray-500";
export const FILTER_INPUT = "mt-1 h-8 w-full rounded-md border border-[#EBECE6] bg-white px-2.5 text-[13px] text-[#253439]";
// One scale for the whole Catalog page: buttons 28px high with 12px type (the size of the popup's buttons), table cells 12px by 8px.
export const BTN = "inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md border border-[#EBECE6] bg-white px-3 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-40";
export const BTN_FIELD = BTN.replace("h-7", "h-8"); // beside the 32px inputs
export const BTN_PRIMARY = "inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md bg-cyan-500 px-3 text-xs font-semibold text-white transition hover:bg-cyan-600 disabled:opacity-50";
export const CELL = "px-3 py-2 align-middle";
export const HEAD_CELL = "px-3 py-2 font-semibold whitespace-nowrap";
const TEXT_FILTERS = Object.freeze([
  { key: "problem", label: F.problem, hint: F.problemHint },
  { key: "keyword", label: F.keyword, hint: F.keywordHint },
  { key: "symptom", label: F.symptom, hint: F.symptomHint },
]);

const pillClass = (active) => `rounded px-3 py-0.5 text-xs font-semibold transition ${
  active ? "bg-cyan-500 text-white" : "text-gray-500 hover:text-gray-700"}`;

/** A segmented choice of `options` ({key, label}); `value` is the pressed one. */
function Segmented({ label, options, value, onChange }) {
  return (
    <div role="group" aria-label={label} className="inline-flex rounded-lg border border-[#EBECE6] bg-white p-0.5">
      {options.map((o) => (
        <button key={o.key} type="button" aria-pressed={value === o.key} onClick={() => onChange(o.key)}
          className={pillClass(value === o.key)}>{o.label}</button>
      ))}
    </div>
  );
}

/** Conversations | Manual: which view the page shows. */
export function ViewToggle({ value, onChange }) {
  return <Segmented label={CATALOG_TEXT.view} options={VIEW_OPTIONS} value={value} onChange={onChange} />;
}

/** The product then its category dropdowns (both from the products tables). `withCounts` adds each category's entry count. */
export function ProductCategoryFilter({ product, category, products, onChange, withCounts = true }) {
  const id = useId();
  const chosen = products.find((p) => p.product.slug === product);
  return (
    <>
      <div>
        <label htmlFor={`${id}-product`} className={FILTER_LABEL}>{F.product}</label>
        <select id={`${id}-product`} className={FILTER_INPUT} value={product} onChange={(e) => onChange("product", e.target.value)}>
          <option value="">{F.allProducts}</option>
          {products.map(({ product: p }) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor={`${id}-category`} className={FILTER_LABEL}>{F.category}</label>
        <select id={`${id}-category`} className={FILTER_INPUT} value={category} disabled={!chosen}
          onChange={(e) => onChange("category", e.target.value)}>
          <option value="">{F.allCategories}</option>
          {(chosen ? chosen.categories : []).map((c) => (
            <option key={c.slug} value={c.slug}>{withCounts ? F.option(c.name, c.count) : c.name}</option>
          ))}
        </select>
      </div>
    </>
  );
}

/** The Manual view's filters bar: product then its category, three text searches and the state toggle. */
export default function CatalogFilters({ filters, products, onChange, onClear }) {
  const id = useId();
  return (
    <section aria-label={F.label} className="mb-4 rounded-xl border border-[#EBECE6] bg-white p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <ProductCategoryFilter product={filters.product} category={filters.category} products={products} onChange={onChange} />
        {TEXT_FILTERS.map((t) => (
          <div key={t.key}>
            <label htmlFor={`${id}-${t.key}`} className={FILTER_LABEL}>{t.label}</label>
            <input id={`${id}-${t.key}`} type="search" className={FILTER_INPUT} maxLength={FILTER_MAX_CHARS} placeholder={t.hint}
              value={filters[t.key]} onChange={(e) => onChange(t.key, e.target.value)} />
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <Segmented label={F.state} options={STATE_OPTIONS} value={filters.state} onChange={(v) => onChange("state", v)} />
        <button type="button" onClick={onClear} className={BTN}>{F.clear}</button>
      </div>
    </section>
  );
}
